import React, { useEffect, useState, useCallback } from "react";
import cx from "classnames";
import { useEmblaCarousel } from "embla-carousel/react";
import { Image } from "src/components/image";

import styles from "./image-carousel.module.css";

const DotButton = ({
  selected,
  onClick,
}: {
  selected: boolean;
  onClick: () => void;
}) => (
  <button
    className={`embla__dot${selected ? " is-selected" : ""}`}
    onClick={onClick}
  />
);

interface Media {
  _key: string;
  alt?: string;
  asset?: {
    _id: string;
  };
  image?: {
    asset: {
      _id: string;
      url: string;
    };
  };
  video?: {
    asset: {
      url: string;
    };
  };
  showInlineOnMobile?: boolean;
}
const Media = ({ data }: { data: Media }) => {
  if (data.video?.asset?.url) {
    return (
      <div className={styles.videoContainer}>
        <video
          loop
          autoPlay
          muted
          playsInline
          controls={false}
          poster={data.image?.asset?.url}
          preload="auto"
        >
          <source src={data.video.asset.url} type="video/mp4" />
          {data.image?.asset._id && (
            <Image
              key={data._key}
              className={styles.productImage}
              imageId={data.image.asset._id}
              alt={data.alt}
              style={{
                height: "100%",
              }}
              objectFit="cover"
            />
          )}
        </video>
      </div>
    );
  } else if (data.image?.asset?._id) {
    return (
      <Image
        key={data._key}
        className={styles.productImage}
        imageId={data.image.asset._id}
        alt={data.image.alt}
        style={{
          height: "100%",
        }}
        objectFit="cover"
      />
    );
  } else {
    return null;
  }
};

interface ProductImageCarouselProps {
  className?: string;
  images: [
    {
      _key?: string;
      alt?: string;
      asset?: {
        _id: string;
      };
      image?: {
        alt: string;
        asset: {
          _id: string;
          url: string;
        };
      };
      showInlineOnMobile?: boolean;
    }
  ];
}

export const ProductImageCarousel = ({
  className,
  images,
}: ProductImageCarouselProps) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    slidesToScroll: 1,
    align: "start",
  });

  const [prevBtnEnabled, setPrevBtnEnabled] = useState(false);
  const [nextBtnEnabled, setNextBtnEnabled] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState([]);

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [
    emblaApi,
  ]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [
    emblaApi,
  ]);
  const scrollTo = useCallback(
    (index) => emblaApi && emblaApi.scrollTo(index),
    [emblaApi]
  );

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
    setPrevBtnEnabled(emblaApi.canScrollPrev());
    setNextBtnEnabled(emblaApi.canScrollNext());
  }, [emblaApi, setSelectedIndex]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    setScrollSnaps(emblaApi.scrollSnapList());
    emblaApi.on("select", onSelect);
  }, [emblaApi, setScrollSnaps, onSelect]);

  return (
    <div className={cx(className, styles.imageCarousel)}>
      <div className={cx("embla", styles.carouselContainer)}>
        <div className="embla__viewport" ref={emblaRef}>
          <div style={{ display: "flex" }}>
            {images &&
              images.map((productImage) => {
                // Main image
                if (productImage?.asset?._id) {
                  return (
                    <div className={cx("embla__slide", styles.carouselSlide)}>
                      <Media data={{ image: productImage }} />
                    </div>
                  );
                }
                // Content module images
                if (
                  productImage?.image?.asset?._id &&
                  !productImage?.showInlineOnMobile
                ) {
                  return (
                    <div className={cx("embla__slide", styles.carouselSlide)}>
                      <Media data={productImage} />
                    </div>
                  );
                }
              })}
          </div>
        </div>
      </div>
      <div className="embla__dots">
        {scrollSnaps.map((snap, index) => (
          <DotButton
            selected={index === selectedIndex}
            onClick={() => scrollTo(index)}
            key={index}
          />
        ))}
      </div>
    </div>
  );
};
